/**
 * Reading the reviewer's 1st Choice back out of the tender, for the step that shows it.
 *
 * ⚠️ TWO STORES HELD THE SAME DECISION AND ONLY ONE OF THEM WAS WRITTEN.
 *
 * Compare & Select records every pick in `tender.ranked_offer_selections`. Picking rank 1 is
 * ALSO supposed to mirror itself onto the offer row as `is_recommended` / `recommended_months`,
 * for the steps that predate the ranked store. That mirror write is conditional — it needs a
 * tenderId, an org, and a non-empty site map, and it is awaited inside the click handler — so
 * it can simply not happen, and nothing tells anyone.
 *
 * When it does not, the two stores disagree and the workflow dead-ends: Compare & Select shows
 * all three picks selected, Recommended Offer gates on `recommendedMonths.length > 0`, finds
 * none, and prints "No Recommended Offers — please go back to Step 2 and select recommended
 * contract lengths". Going back cannot help, because the picks ARE there; clicking them again
 * only deselects them.
 *
 * Seen on prod tender 23947ac5 (GIN GIN AG PTY LTD): ranks 1, 2 and 3 all stored, and every one
 * of the eight offer rows carrying `is_recommended: false` and `recommended_months: []`.
 *
 * So the ranked store is read directly rather than trusted to have been mirrored. The offer rows
 * are left exactly as they are wherever they already carry a value — this only fills in what the
 * mirror failed to write, so a tender where it worked is untouched.
 */

type RankedSelection = {
  rank: number;
  offerId?: string;
  termMonths?: number;
  siteId?: string;
  retailerName?: string;
};

type OfferLike = {
  id?: string;
  retailerName?: string;
  siteId?: string;
  isRecommended?: boolean;
  recommendedMonths?: number[];
  extn?: Record<string, unknown> | null;
};

/**
 * One legal entity across every site — the tender then has ONE set of rank slots rather than a
 * set per site.
 *
 * ⚠️ THE SINGLE COPY. This predicate decides how a selection is matched to an offer, and it was
 * written out separately in each step that needed it. Two readers of one rule is exactly how the
 * Continue button came to demand a selection shape the writer had stopped producing.
 */
export const isSharedAbnTender = (sites: ReadonlyArray<{ abn?: string | null }>): boolean =>
  sites.length > 1 && sites.every(s => !!s.abn?.trim() && s.abn.trim() === sites[0].abn?.trim());

/** Does this rank-1 selection describe this offer? Mirrors how the pick was made. */
const selectionMatchesOffer = (
  selection: RankedSelection,
  offer: OfferLike,
  allSitesSameAbn: boolean,
): boolean => {
  // Shared: the pick is the retailer, and every site has its own row for that retailer.
  if (allSitesSameAbn) {
    return !!selection.retailerName && selection.retailerName === offer.retailerName;
  }
  // Per-site: the pick is one specific row. Selections store the PARENT (pre-grouping) offer id.
  const parentId = (offer.extn as Record<string, unknown> | undefined)?.parentOfferId;
  return !!selection.offerId
    && (selection.offerId === offer.id || selection.offerId === parentId);
};

/**
 * Offers with the reviewer's 1st Choice filled in from the ranked store.
 *
 * Only ever ADDS: an offer that already states a recommended term keeps it untouched, and an
 * offer no rank-1 selection describes is returned unchanged. Returns the input array itself when
 * nothing needed filling, so React sees the same identity and does not re-render.
 */
export const applyRankedSelectionsToOffers = <T extends OfferLike>(
  offers: ReadonlyArray<T>,
  selections: ReadonlyArray<RankedSelection>,
  allSitesSameAbn: boolean,
): T[] | ReadonlyArray<T> => {
  const rankOne = selections.filter(s => s.rank === 1 && Number.isFinite(s.termMonths));
  if (rankOne.length === 0) return offers;

  let changed = false;
  const filled = offers.map(offer => {
    if ((offer.recommendedMonths?.length ?? 0) > 0) return offer;   // already stated — never overwrite
    const match = rankOne.find(s => selectionMatchesOffer(s, offer, allSitesSameAbn));
    if (!match) return offer;
    changed = true;
    return { ...offer, recommendedMonths: [match.termMonths as number], isRecommended: true };
  });
  return changed ? filled : offers;
};

/**
 * What `is_recommended` / `recommended_months` SHOULD read on every offer of the tender.
 *
 * ⚠️ THE MIRROR USED TO BE WRITTEN FROM THE CLICK, AND COULD SIMPLY NOT HAPPEN.
 *
 * The rank-1 branch of handleSelectRank rewrote every offer row through saveOffers, behind four
 * conditions that each exit silently — a tenderId, an org, a NON-EMPTY SITE MAP, and a catch
 * that swallows whatever is left. The site map is built by comparing `sites[].id`, which holds a
 * tender_site id, against `tender_site.site_id`, which holds a site-table id: two different id
 * spaces that never match, so the map only ever fills from its NMI fallback. On prod tender
 * 23947ac5 it did not fill, every offer kept `is_recommended: false`, and the Recommended Offer
 * step dead-ended on a tender whose three picks were all stored.
 *
 * So the mirror is derived from the ranked store instead of from the click, and written beside
 * it — same place, same moment, no site map and no row rewrite.
 *
 * Returns an entry for EVERY offer, including the ones that should now be clear: deselecting a
 * 1st Choice has to retract the flag, not just stop setting it.
 *
 * All three tender shapes go through the one matching rule:
 *   single site            → per-site, matched on the offer row itself
 *   multi-site, differing  → per-site, each site its own 1st Choice
 *   multi-site, one ABN    → shared, the pick is the retailer, on every site's row
 */
export const recommendedFlagsForOffers = <T extends OfferLike>(
  offers: ReadonlyArray<T>,
  selections: ReadonlyArray<RankedSelection>,
  allSitesSameAbn: boolean,
): Array<{ offerId: string; months: number[] }> => {
  const rankOne = selections.filter(s => s.rank === 1 && Number.isFinite(s.termMonths));
  return offers
    .filter((o): o is T & { id: string } => !!o.id)
    .map(offer => {
      const match = rankOne.find(s => selectionMatchesOffer(s, offer, allSitesSameAbn));
      return { offerId: offer.id, months: match ? [match.termMonths as number] : [] };
    });
};
